import React, { useRef, useState, useEffect, useContext } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import {
  Student,
  Location,
  Path,
  StudentPosition,
  StudentFormData,
} from '../../../types';
import { StudentMarker } from './StudentMarker';
import { PathAnimation } from './PathAnimation';
import { getStatus, formatTimeRemaining } from '../../../utils/timeUtils';
import {
  Settings,
  Map as MapSprite,
  X,
  Clock,
  Route,
  Edit,
  Trash2,
  List,
  MapPin,
  Wand2,
  Maximize,
  Minimize,
} from 'lucide-react';
import { PathManager } from './PathManager';
import { PathCreator } from './PathCreator';
import { LocationManager } from './LocationManager';
import { BuildingManager } from './BuildingManager';
import { useNow } from '../../../hooks/useNow';

/* ─────────────────────────── StudentCard ─────────────────────────── */

interface StudentCardProps {
  student: Student;
  locations: Location[];
  onMarkReturned?: (studentId: string) => void;
  onClose?: () => void;
}

const StudentCard: React.FC<StudentCardProps> = ({
  student,
  locations,
  onMarkReturned,
  onClose,
}) => {
  const startLocation = locations.find(
    (loc) => loc.id === student.startLocationId,
  );
  const endLocation = locations.find(
    (loc) => loc.id === student.destinationId,
  );

  const now = useNow();
  const status = getStatus(student.expectedReturnTime);
  const timeRemaining = formatTimeRemaining(student.expectedReturnTime - now);

  const statusText =
    status === 'overdue'
      ? 'Overdue'
      : status === 'warning'
      ? 'Returning Soon'
      : 'On Time';

  const statusColor =
    status === 'overdue'
      ? 'text-red-600'
      : status === 'warning'
      ? 'text-amber-600'
      : 'text-green-600';

  return (
    <div className="bg-stone-50 rounded-lg p-4 border border-stone-200 flex items-start gap-4 relative">
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-stone-400 hover:text-stone-600"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <div className="flex flex-col items-center">
        <img
          src={student.avatar || 'https://via.placeholder.com/48'}
          alt={student.name}
          className="w-12 h-12 rounded-full mb-2 object-cover"
        />
        <h3 className="font-semibold text-stone-800 text-center">
          {student.name}
        </h3>
      </div>

      <div className="flex-1">
        <p className="text-sm text-stone-600">
          From:&nbsp;{startLocation?.name ?? 'Unknown'}
        </p>
        <p className="text-sm text-stone-600">
          To:&nbsp;{endLocation?.name ?? 'Unknown'}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <Clock className="h-4 w-4 text-stone-400" />
          <span className={`text-sm font-medium ${statusColor}`}>
            {timeRemaining} ({statusText})
          </span>
        </div>
      </div>

      {onMarkReturned && (
        <button
          onClick={() => onMarkReturned(student.userId)}
          className="ml-4 px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
        >
          Mark as Returned
        </button>
      )}
    </div>
  );
};

/* ───────────────────── ActivePassRow (standardized list style) ───────────────────── */
interface ActivePassRowProps {
  student: Student;
  locations: Location[];
  onEnd?: (studentId: string) => void;
  canEnd?: boolean;
}

const ActivePassRow: React.FC<ActivePassRowProps> = ({ student, locations, onEnd, canEnd }) => {
  const endLocation = locations.find((loc) => loc.id === student.destinationId);
  const now = useNow();
  const status = getStatus(student.expectedReturnTime);
  const timeRemainingMs = student.expectedReturnTime - now;
  const timeRemaining = formatTimeRemaining(timeRemainingMs);
  const pillColor =
    status === 'overdue'
      ? 'bg-red-100 text-red-700'
      : status === 'warning'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-amber-50 text-amber-700';
  const startTimeStr = (() => {
    try {
      return new Date(student.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  })();

  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-stone-50 transition-colors border border-transparent hover:border-stone-200">
      <div className="flex items-center gap-4 min-w-0">
        <img
          src={student.avatar || 'https://via.placeholder.com/48'}
          alt={student.name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
        <div className="min-w-0">
          <p className="font-semibold text-stone-800 truncate">{student.name}</p>
          {student.grade && (
            <p className="text-sm text-stone-500 leading-tight">{student.grade}</p>
          )}
          <p className="text-sm text-stone-600 leading-tight mt-1 truncate">
            {endLocation?.name || student.destinationName || 'Unknown'}
          </p>
          <p className="text-xs text-stone-400 leading-tight">Started at {startTimeStr}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <span className={`text-sm font-medium px-3 py-1 rounded-full inline-flex items-center gap-1 ${pillColor}`}>
          <Clock className="h-4 w-4" /> {timeRemaining} {status === 'overdue' ? '' : 'remaining'}
        </span>
  {onEnd && canEnd && (
          <button
            onClick={() => onEnd(student.userId)}
            className="text-red-600 text-sm font-medium hover:underline"
          >
            End Pass
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────── MapView ─────────────────────────── */

interface MapViewProps {
  students: Student[];
  locations: Location[];
  paths: Path[];
  studentPositions: Record<string, StudentPosition>;
  editingMap: boolean;
  setEditingMap: (editing: boolean) => void;
  mapImage: string;
  updateLocationPosition: (
    locationId: string,
    position: { x: number; y: number },
  ) => void;
  updatePathWaypoints: (
    pathId: string,
    waypoints: { x: number; y: number }[],
  ) => Promise<void>;
  addStudent: (data: StudentFormData) => Promise<void>;
  addPath: (
    startLocationId: string,
    endLocationId: string,
    waypoints: { x: number; y: number }[],
  ) => Promise<string>;
  deletePath?: (pathId: string) => Promise<void>;
  addLocation: (location: Omit<Location, 'id'>) => Promise<string>;
  updateLocation: (location: Location) => Promise<void>;
  deleteLocation: (locationId: string) => Promise<void>;
  markStudentReturned: (studentId: string) => Promise<void>;
  saveMapConfig?: () => void;
  isFullScreen?: boolean;
  onToggleFullScreen?: () => void;
}

export const MapView: React.FC<MapViewProps> = ({
  students,
  locations,
  paths,
  studentPositions,
  editingMap,
  setEditingMap,
  mapImage,
  updateLocationPosition,
  updatePathWaypoints,
  addStudent,
  addPath,
  deletePath = () => {},
  addLocation,
  updateLocation,
  deleteLocation,
  markStudentReturned,
  saveMapConfig,
  isFullScreen = false,
  onToggleFullScreen,
}) => {
  /* ───────────────────────── state & refs ───────────────────────── */

  void saveMapConfig;
  const mapRef = useRef<HTMLDivElement>(null);

  const [draggingLocation, setDraggingLocation] = useState<string | null>(null);
  const [_mapSize, setMapSize] = useState({ width: 1920, height: 1080 });

  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [showActivePasses, setShowActivePasses] = useState(false);

  const [showPathManager, setShowPathManager] = useState(false);
  const [showPathCreator, setShowPathCreator] = useState(false);
  const [showManagePaths, setShowManagePaths] = useState(false);

  const [editingPathId, setEditingPathId] = useState<string | null>(null);
  const [pathEditMode, setPathEditMode] = useState(false);
  const [pathEditWaypoints, setPathEditWaypoints] = useState<
    { x: number; y: number }[]
  >([]);

  const [pathCreationMode, setPathCreationMode] = useState(false);
  const [pathStartLocation, setPathStartLocation] =
    useState<Location | null>(null);
  const [pathWaypoints, setPathWaypoints] = useState<
    { x: number; y: number }[]
  >([]);

  const [showLocationManager, setShowLocationManager] = useState(false);
  const [showBuildingManager, setShowBuildingManager] = useState(false);
  const [locationToMove, setLocationToMove] = useState<string | null>(null);

  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [draggingWaypointIdx, setDraggingWaypointIdx] = useState<number | null>(
    null,
  );

  const [_hoveredLocation, setHoveredLocation] = useState<string | null>(null);
  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);

  const { hasClaim, userProfile } = useContext(UserContext);
  const role = userProfile?.mainRole || '';
  const currentUserId = userProfile?.userId;
  const canEndForStudent = (s: Student) => {
    if (!s) return false;
    if (role.includes('CAMPUSSECURITYOFFICER')) return true;
    if (role.includes('SUPERTEACHER')) return true;
    if (role.includes('TEACHER') && s.teacherId && s.teacherId === currentUserId) return true;
    return false;
  };

  /* ─────────────────────── window-resize effect ─────────────────────── */

  useEffect(() => {
    const updateSize = () => {
      if (mapRef.current) {
        setMapSize({ width: 1920, height: 1080 });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  /* ─────────────────────────── handlers (same as original) ─────────────────────────── */

  const handleMouseMove = (e: React.MouseEvent) => {
    if (pathEditMode && draggingWaypointIdx !== null && mapRef.current) {
      const rect = mapRef.current.getBoundingClientRect();
      const x = mapRef.current.scrollLeft + (e.clientX - rect.left);
      const y = mapRef.current.scrollTop + (e.clientY - rect.top);
      setPathEditWaypoints((prev) =>
        prev.map((wp, i) => (i === draggingWaypointIdx ? { x, y } : wp)),
      );
      return;
    }

    if (!editingMap || !draggingLocation || !mapRef.current) return;

    const rect = mapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    updateLocationPosition(draggingLocation, { x, y });
  };

  const handleMouseUp = () => {
    setDraggingLocation(null);
    setDraggingWaypointIdx(null);
  };

  const handleLocationMouseDown = (
    locationId: string,
    e: React.MouseEvent,
  ) => {
    if (!editingMap) {
      setSelectedLocation(locationId === selectedLocation ? null : locationId);
      return;
    }
    e.preventDefault();
    setDraggingLocation(locationId);
  };

  const handleWaypointMouseDown = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingMap || !pathEditMode) return;
    setDraggingWaypointIdx(index);
  };

  const handleDeletePath = async (pathId: string) => {
    try {
      await deletePath(pathId);
      setSelectedPathId(null);
    } catch (error) {
      console.error('Failed to delete path:', error);
    }
  };

  const handleLocationClick = (location: Location, e: React.MouseEvent) => {
    if (!editingMap) {
      setSelectedLocation(location.id === selectedLocation ? null : location.id);
      return;
    }

    if (pathCreationMode) {
      e.stopPropagation();
      if (!pathStartLocation) {
        setPathStartLocation(location);
      } else if (location.id !== pathStartLocation.id) {
        addPath(pathStartLocation.id, location.id, pathWaypoints).catch(
          console.error,
        );
        setPathStartLocation(null);
        setPathWaypoints([]);
        setPathCreationMode(false);
        setEditingMap(false);
      }
    } else {
      handleLocationMouseDown(location.id, e);
    }
  };

  const handleLocationMouseEnter = (locationId: string) => {
    setHoveredLocation(locationId);
  };
  const handleLocationMouseLeave = () => setHoveredLocation(null);

  const handleMapClick = (e: React.MouseEvent) => {
    if (locationToMove) {
      handleMapClickForLocationMove(e);
      return;
    }
    if (
      !editingMap ||
      !pathCreationMode ||
      !pathStartLocation ||
      !mapRef.current
    )
      return;

    e.stopPropagation();
    const rect = mapRef.current.getBoundingClientRect();
    const x = mapRef.current.scrollLeft + (e.clientX - rect.left);
    const y = mapRef.current.scrollTop + (e.clientY - rect.top);
    setPathWaypoints((prev) => [...prev, { x, y }]);
  };

  const handlePathCreationToggle = () => {
    if (pathCreationMode) {
      setPathStartLocation(null);
      setPathWaypoints([]);
    }
    setPathCreationMode(!pathCreationMode);
  };

  const handleEditPathWaypoints = (pathId: string) => {
    const path = paths.find((p) => p.id === pathId);
    if (!path) return;

    setSelectedPathId(null);
    setEditingPathId(pathId);
    setPathEditWaypoints([...path.waypoints]);
    setPathEditMode(true);
    setShowManagePaths(false);
    setEditingMap(true);
  };

  const handleCancelPathEdit = () => {
    setEditingPathId(null);
    setPathEditMode(false);
    setPathEditWaypoints([]);
  };

  const handleSavePathEdit = () => {
    if (!editingPathId) return;
    updatePathWaypoints(editingPathId, pathEditWaypoints);
    setEditingPathId(null);
    setPathEditMode(false);
    setPathEditWaypoints([]);
  };

  const handlePathEditMapClick = (e: React.MouseEvent) => {
    if (!editingMap || !pathEditMode || !editingPathId || !mapRef.current)
      return;
    e.stopPropagation();
    const rect = mapRef.current.getBoundingClientRect();
    const x = mapRef.current.scrollLeft + (e.clientX - rect.left);
    const y = mapRef.current.scrollTop + (e.clientY - rect.top);
    setPathEditWaypoints((prev) => [...prev, { x, y }]);
  };

  const handleDeleteWaypoint = (index: number) =>
    setPathEditWaypoints((prev) => prev.filter((_, i) => i !== index));

  const handleMoveLocationOnMap = (locationId: string) => {
    setLocationToMove(locationId);
    setShowLocationManager(false);
  };

  const handleMapClickForLocationMove = (e: React.MouseEvent) => {
    if (!locationToMove || !mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    updateLocationPosition(locationToMove, { x, y });
    const location = locations.find((loc) => loc.id === locationToMove);
    if (location) {
      updateLocation({ ...location, position: { x, y } }).catch(console.error);
    }
    setLocationToMove(null);
  };

  useEffect(() => {
    if (!locationToMove) return;
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLocationToMove(null);
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [locationToMove]);

  /* Demo students helper */
  const harryPotterNames = [
    'Harry Potter',
    'Hermione Granger',
    'Ron Weasley',
    'Draco Malfoy',
    'Luna Lovegood',
    'Neville Longbottom',
    'Ginny Weasley',
    'Fred Weasley',
    'George Weasley',
    'Cho Chang',
    'Cedric Diggory',
    'Seamus Finnigan',
    'Dean Thomas',
    'Lavender Brown',
    'Parvati Patil',
    'Padma Patil',
    'Pansy Parkinson',
    'Gregory Goyle',
    'Vincent Crabbe',
    'Susan Bones',
  ];

  const addDemoStudents = () => {
    const classroomLocations = locations.filter((l) => l.type === 'classroom');
    const destinationLocations = locations.filter((l) => l.type !== 'classroom');
    if (!classroomLocations.length || !destinationLocations.length) return;

    const shuffledNames = [...harryPotterNames]
      .sort(() => 0.5 - Math.random())
      .slice(0, 15);

    shuffledNames.forEach((name, idx) => {
      setTimeout(() => {
        const classroom =
          classroomLocations[
            Math.floor(Math.random() * classroomLocations.length)
          ];
        const destination =
          destinationLocations[
            Math.floor(Math.random() * destinationLocations.length)
          ];
        addStudent({
          name,
          startLocationId: classroom.id,
          destinationId: destination.id,
        });
      }, idx * 4600);
    });
  };

  /* ─────────────────────────── render ─────────────────────────── */

  return (
    <>
      <div className="relative w-full h-full">
        {/* Main map canvas */}
        <div
          ref={mapRef}
          className={`w-full h-full bg-amber-50 rounded-lg border overflow-x-auto ${
            editingMap ? 'border-amber-600 cursor-move' : 'border-amber-200'
          } ${pathCreationMode || pathEditMode ? 'cursor-crosshair' : ''} aspect-video`}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Inner fixed-size canvas */}
          <div className="min-w-[1920px] relative">
            {/* Background image */}
            <div
              className="absolute inset-0 opacity-50 w-[1920px] h-[1080px]"
              style={{
                backgroundImage: `url(${mapImage})`,
                backgroundSize: '1920px 1080px',
                backgroundRepeat: 'no-repeat',
              }}
            />

            {/* Foreground drawing layer */}
            <div
              className="relative z-10 w-[1920px] h-[1080px]"
              onClick={handleMapClick}
            >
              {/* Paths (dashed polylines) */}
              {paths.map((path) => {
                const startLocation = locations.find(
                  (l) => l.id === path.startLocationId,
                );
                const endLocation = locations.find(
                  (l) => l.id === path.endLocationId,
                );
                if (!startLocation || !endLocation) return null;

                const fullPath = [
                  startLocation.position,
                  ...path.waypoints,
                  endLocation.position,
                ];

                return (
                  <div key={path.id} className="absolute inset-0">
                    <svg className="absolute inset-0 pointer-events-none">
                      <polyline
                        points={fullPath.map((p) => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke="#78716c"
                        strokeWidth={2}
                        strokeDasharray="4,4"
                        strokeOpacity={0.5}
                      />
                    </svg>
                  </div>
                );
              })}

              {/* Student path animations */}
              {students
                .filter((s) => !s.returned)
                .map((student) => {
                  const position = studentPositions[student.userId];
                  const path = paths.find((p) => p.id === student.pathId);
                  if (!position || !path) return null;

                  return (
                    <PathAnimation
                      key={`path-${student.userId}`}
                      student={student}
                      path={path}
                      locations={locations}
                      progress={position.progress}
                    />
                  );
                })}

              {/* Location dots */}
              {locations.map((location) => {
                const isClassroom = location.type === 'classroom';
                const isBuilding = location.type === 'building';
                const isDestination = !isClassroom;

                const buildingStudentCount = isBuilding
                  ? students.filter((s) => {
                      const classroom = locations.find(
                        (l) => l.id === s.startLocationId,
                      );
                      return !s.returned && classroom?.buildingId === location.id;
                    }).length
                  : 0;

                const destinationStudentCount = isDestination
                  ? students.filter(
                      (s) => !s.returned && s.destinationId === location.id,
                    ).length
                  : 0;

                return (
                  <div
                    key={location.id}
                    className={`absolute p-2 transform -translate-x-1/2 -translate-y-1/2 ${
                      isClassroom
                        ? `bg-amber-600 text-white h-5 w-5 ${
                            students.some(
                              (s) =>
                                !s.returned && s.startLocationId === location.id,
                            )
                              ? 'opacity-100'
                              : 'opacity-10'
                          } ${
                            students.some(
                              (s) =>
                                !s.returned &&
                                s.startLocationId === location.id &&
                                s.expectedReturnTime < Date.now(),
                            )
                              ? 'ring-2 ring-red-500'
                              : ''
                          }`
                        : location.type === 'restroom'
                        ? 'bg-blue-600 text-white h-10 w-10'
                        : location.type === 'other'
                        ? 'bg-purple-600 text-white h-10 w-10'
                        : 'bg-amber-600 text-white h-10 w-10'
                    } ${
                      selectedLocation === location.id
                        ? 'ring-4 ring-yellow-300 z-30'
                        : pathCreationMode &&
                          pathStartLocation?.id === location.id
                        ? 'ring-4 ring-green-300 z-30'
                        : pathCreationMode &&
                          pathStartLocation &&
                          location.id !== pathStartLocation.id
                        ? 'ring-2 ring-amber-400 hover:ring-4 hover:ring-red-500 z-30 cursor-pointer'
                        : editingMap
                        ? 'cursor-move z-30'
                        : 'cursor-pointer'
                    } rounded-full flex items-center justify-center shadow-md transition-all hover:scale-110`}
                    style={{
                      left: `${location.position.x}px`,
                      top: `${location.position.y}px`,
                    }}
                    onClick={(e) => handleLocationClick(location, e)}
                    onMouseEnter={() => handleLocationMouseEnter(location.id)}
                    onMouseLeave={handleLocationMouseLeave}
                    title={location.name}
                  >
                    {/* Building / destination count badges */}
                    {isBuilding && buildingStudentCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center z-10 border-2 border-white">
                        {buildingStudentCount}
                      </span>
                    )}
                    {isDestination && !isBuilding && destinationStudentCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center z-10 border-2 border-white">
                        {destinationStudentCount}
                      </span>
                    )}
                    <span className="text-xs font-bold truncate max-w-[32px] flex items-center justify-center w-full h-full">
                      {isClassroom
                        ? students.filter(
                            (s) =>
                              !s.returned && s.startLocationId === location.id,
                          ).length || ''
                        : isBuilding
                        ? location.name.length > 10
                          ? location.name
                              .split(' ')
                              .map((w) => w[0])
                              .join('')
                          : location.name
                        : location.name.split(' ')[0][0]}
                    </span>
                  </div>
                );
              })}

              {/* Student markers */}
              {students
                .filter((s) => !s.returned)
                .map((student) => {
                  const position = studentPositions[student.userId];
                  if (!position) return null;
                  const status = getStatus(student.expectedReturnTime);
                  return (
                    <StudentMarker
                      key={student.userId}
                      student={student}
                      position={position.position}
                      status={status}
                      locations={locations}
                      onSelect={setSelectedStudent}
                    />
                  );
                })}

              {/* Dynamic path creation polyline */}
              {pathCreationMode && pathStartLocation && (
                <>
                  <div className="absolute inset-0 z-20 pointer-events-none">
                    <svg className="absolute inset-0 w-[1920px] h-[1080px]">
                      <polyline
                        points={[
                          pathStartLocation.position,
                          ...pathWaypoints,
                        ]
                          .map((p) => `${p.x},${p.y}`)
                          .join(' ')}
                        fill="none"
                        stroke="#78716c"
                        strokeWidth={2}
                        strokeDasharray="4,4"
                      />
                      {pathWaypoints.map((wp, idx) => (
                        <g key={idx}>
                          <circle
                            cx={wp.x}
                            cy={wp.y}
                            r="8"
                            fill="#ffffff"
                            stroke="#78716c"
                            strokeWidth="2"
                          />
                          <circle cx={wp.x} cy={wp.y} r="4" fill="#78716c" />
                        </g>
                      ))}
                    </svg>
                  </div>
                  <div
                    className="absolute inset-0 z-15 cursor-crosshair"
                    onClick={handleMapClick}
                  />
                </>
              )}

              {/* Path editing layer */}
              {pathEditMode && editingPathId && (
                <>
                  <div className="absolute inset-0 z-20">
                    <svg className="absolute inset-0 w-[1920px] h-[1080px]">
                      {(() => {
                        const path = paths.find((p) => p.id === editingPathId);
                        if (!path) return null;
                        const startLocation = locations.find(
                          (l) => l.id === path.startLocationId,
                        );
                        const endLocation = locations.find(
                          (l) => l.id === path.endLocationId,
                        );
                        if (!startLocation || !endLocation) return null;
                        return (
                          <polyline
                            className="pointer-events-none"
                            points={[
                              startLocation.position,
                              ...pathEditWaypoints,
                              endLocation.position,
                            ]
                              .map((p) => `${p.x},${p.y}`)
                              .join(' ')}
                            fill="none"
                            stroke="#78716c"
                            strokeWidth={2}
                            strokeDasharray="4,4"
                          />
                        );
                      })()}
                      {pathEditWaypoints.map((wp, idx) => (
                        <g key={idx}>
                          <circle
                            cx={wp.x}
                            cy={wp.y}
                            r="8"
                            fill="#ffffff"
                            stroke="#78716c"
                            strokeWidth="2"
                            className="pointer-events-auto cursor-move"
                            onMouseDown={(e) => handleWaypointMouseDown(idx, e)}
                          />
                          <circle cx={wp.x} cy={wp.y} r="4" fill="#78716c" />
                        </g>
                      ))}
                    </svg>
                    {pathEditWaypoints.map((wp, idx) => (
                      <button
                        key={idx}
                        className="absolute -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full w-3 h-3 text-[10px] flex items-center justify-center"
                        style={{ left: wp.x, top: wp.y - 15 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteWaypoint(idx);
                        }}
                      >
                        ×
                      </button>
                    ))}
                  </div>
                  <div
                    className="absolute inset-0 z-15 cursor-crosshair"
                    onClick={handlePathEditMapClick}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Floating Student card */}
        {selectedStudent && (
          <div className="absolute left-4 top-4 z-20">
            <StudentCard
              student={selectedStudent}
              locations={locations}
              onClose={() => setSelectedStudent(null)}
              onMarkReturned={(id) => {
                markStudentReturned(id).catch(console.error);
                setSelectedStudent(null);
              }}
            />
          </div>
        )}

        {/* Top-right controls */}
        <div className="absolute right-4 top-4 flex gap-2 z-20">
          <button
            onClick={() => setShowActivePasses(true)}
            className="p-3 rounded-full shadow-lg bg-white/90 text-green-600 hover:bg-green-50 transition-all relative"
            title="Active Hall Passes"
          >
            <MapSprite className="h-5 w-5" />
            {students.filter((s) => !s.returned).length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {students.filter((s) => !s.returned).length}
              </span>
            )}
          </button>
          {onToggleFullScreen && (
            <button
              onClick={onToggleFullScreen}
              className="p-3 rounded-full shadow-lg bg-white/90 text-blue-600 hover:bg-blue-50 transition-all"
              title={isFullScreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullScreen ? (
                <Minimize className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </button>
          )}
        </div>

        {/* Bottom-right settings */}
        <div className="absolute right-4 bottom-4 flex flex-col gap-2 z-20">
          {hasClaim('CanManageHallPasses') && (
            <button
              onClick={() => setEditingMap(!editingMap)}
              className={`p-3 rounded-full shadow-lg transition-all ${
                editingMap
                  ? 'bg-amber-600 text-white'
                  : 'bg-white/90 text-amber-600 hover:bg-amber-50'
              }`}
              title={editingMap ? 'Exit Edit Mode' : 'Enter Edit Mode'}
            >
              <Settings className="h-5 w-5" />
            </button>
          )}

          {editingMap && (
            <>
              <button
                onClick={() => setShowLocationManager(true)}
                className="p-3 rounded-full shadow-lg bg-white/90 text-amber-600 hover:bg-amber-50 transition-all"
                title="Manage Locations"
              >
                <MapPin className="h-5 w-5" />
              </button>

              <button
                onClick={handlePathCreationToggle}
                className={`p-3 rounded-full shadow-lg transition-all ${
                  pathCreationMode
                    ? 'bg-amber-600 text-white'
                    : 'bg-white/90 text-amber-600 hover:bg-amber-50'
                }`}
                title={pathCreationMode ? 'Cancel Path Creation' : 'Create New Path'}
              >
                <Route className="h-5 w-5" />
              </button>

              <button
                onClick={() => setShowManagePaths(true)}
                className="p-3 rounded-full shadow-lg bg-white/90 text-amber-600 hover:bg-amber-50 transition-all"
                title="Manage Paths"
              >
                <List className="h-5 w-5" />
              </button>

              <button
                onClick={addDemoStudents}
                disabled={!paths.length}
                className="p-3 rounded-full shadow-lg bg-white/90 text-purple-600 hover:bg-purple-50 transition-all"
                title="Demo: 15 Students Out"
              >
                <Wand2 className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {/* Path-creation guidance */}
        {pathCreationMode && (
          <div className="absolute left-1/2 transform -translate-x-1/2 top-4 bg-white/90 p-2 rounded-md shadow-md z-50">
            <p className="text-sm font-medium text-stone-700">
              {!pathStartLocation
                ? 'Select a starting location'
                : 'Click on the map to add waypoints, then click a destination to finish'}
            </p>
          </div>
        )}

        {/* Path-editing controls */}
        {pathEditMode && editingPathId && (
          <div className="absolute left-1/2 transform -translate-x-1/2 top-4 bg-white/90 p-2 rounded-md shadow-md z-50 flex gap-3 items-center">
            <p className="text-sm font-medium text-stone-700">
              Click the map to add waypoints • red&nbsp;× to remove
            </p>
            <button
              onClick={handleSavePathEdit}
              className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
            >
              Save Path
            </button>
            <button
              onClick={handleCancelPathEdit}
              className="px-3 py-1 bg-stone-400 text-white rounded-md text-sm hover:bg-stone-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Move-location hint */}
        {locationToMove && (
          <div className="absolute left-1/2 transform -translate-x-1/2 top-4 bg-white/90 p-2 rounded-md shadow-md z-50">
            <p className="text-sm font-medium text-stone-700 flex items-center">
              <MapPin className="h-4 w-4 mr-1 text-amber-600" />
              Click to drop location • Esc to cancel
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPathCreator && (
        <PathCreator
          locations={locations}
          onAddPath={addPath}
          onCancel={() => setShowPathCreator(false)}
        />
      )}

      {showPathManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="w-full max-w-2xl">
            <PathManager
              locations={locations}
              paths={paths}
              onAddPath={addPath}
              onUpdatePath={updatePathWaypoints}
              onDeletePath={handleDeletePath}
              onClose={() => setShowPathManager(false)}
            />
          </div>
        </div>
      )}

      {showManagePaths && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-stone-800">Manage Paths</h2>
              <button
                onClick={() => setShowManagePaths(false)}
                className="text-stone-500 hover:text-stone-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {paths.length === 0 ? (
                <p className="text-center text-stone-500 py-8">
                  No paths created yet. Click the route icon in edit mode to add one.
                </p>
              ) : (
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-stone-700 uppercase bg-stone-100">
                    <tr>
                      <th className="px-4 py-2 rounded-tl-lg">From</th>
                      <th className="px-4 py-2">To</th>
                      <th className="px-4 py-2">Waypoints</th>
                      <th className="px-4 py-2 rounded-tr-lg">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paths.map((p) => {
                      const start = locations.find((l) => l.id === p.startLocationId);
                      const end = locations.find((l) => l.id === p.endLocationId);
                      return (
                        <tr
                          key={p.id}
                          className={`hover:bg-stone-50 ${
                            selectedPathId === p.id ? 'bg-amber-50' : ''
                          }`}
                        >
                          <td className="px-4 py-3 border-b border-stone-200">
                            {start?.name ?? 'Unknown'}
                          </td>
                          <td className="px-4 py-3 border-b border-stone-200">
                            {end?.name ?? 'Unknown'}
                          </td>
                          <td className="px-4 py-3 border-b border-stone-200">
                            {p.waypoints.length}
                          </td>
                          <td className="px-4 py-3 border-b border-stone-200">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditPathWaypoints(p.id)}
                                className="p-1 text-amber-600 hover:text-amber-800"
                                title="Edit Waypoints"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeletePath(p.id)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Delete Path"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showActivePasses && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-stone-800 flex items-center gap-2">
                Active Hall Passes
                {students.filter((s) => !s.returned).length > 0 && (
                  <span className="inline-flex items-center justify-center h-6 px-2 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    {students.filter((s) => !s.returned).length}
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowActivePasses(false)}
                className="p-2 rounded-full hover:bg-stone-100 text-stone-500 hover:text-stone-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {students.filter((s) => !s.returned).length === 0 ? (
              <p className="text-stone-500 text-center py-8">No active hall passes</p>
            ) : (
              <div className="divide-y divide-stone-100 max-h-[60vh] overflow-y-auto pr-1 custom-scroll">
                {students
                  .filter((s) => !s.returned)
                  .map((s) => (
                    <ActivePassRow
                      key={s.userId}
                      student={s}
                      locations={locations}
                      canEnd={canEndForStudent(s)}
                      onEnd={(id) => markStudentReturned(id).catch(console.error)}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location & Building managers */}
      {showLocationManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="w-full max-w-3xl">
            <LocationManager
              locations={locations}
              onAddLocation={async (data) => {
                const newLoc = {
                  ...data,
                  position: { x: 960, y: 540 },
                };
                const id = await addLocation(newLoc);
                if (id) handleMoveLocationOnMap(id);
                return id;
              }}
              onUpdateLocation={updateLocation}
              onDeleteLocation={deleteLocation}
              onMoveOnMap={handleMoveLocationOnMap}
              onClose={() => setShowLocationManager(false)}
            />
          </div>
        </div>
      )}

      {showBuildingManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="w-full max-w-3xl">
            <BuildingManager
              locations={locations}
              onAddLocation={async (data) => {
                const newLoc = {
                  ...data,
                  position: { x: 960, y: 540 },
                };
                const id = await addLocation(newLoc);
                if (id) handleMoveLocationOnMap(id);
                return id;
              }}
              onUpdateLocation={updateLocation}
              onDeleteLocation={deleteLocation}
              onMoveOnMap={handleMoveLocationOnMap}
              onClose={() => setShowBuildingManager(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};
